import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Package, Camera, X, Clock, FileCheck, CheckCircle, Truck, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getWRStatusStyle = (status) => {
  const styles = {
    pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', iconType: 'Clock', label: 'En Proceso' },
    invoiced: { bg: 'bg-blue-100', text: 'text-blue-800', iconType: 'FileCheck', label: 'Facturado' },
    paid: { bg: 'bg-green-100', text: 'text-green-800', iconType: 'CheckCircle', label: 'Pagado' },
    shipped: { bg: 'bg-purple-100', text: 'text-purple-800', iconType: 'Truck', label: 'Enviado' },
    in_transit: { bg: 'bg-indigo-100', text: 'text-indigo-800', iconType: 'Truck', label: 'En Transito' },
    delivered: { bg: 'bg-green-200', text: 'text-green-900', iconType: 'CheckCircle', label: 'Entregado' },
    rejected: { bg: 'bg-red-100', text: 'text-red-800', iconType: 'XCircle', label: 'Rechazado' }
  };
  return styles[status] || { bg: 'bg-gray-100', text: 'text-gray-800', iconType: null, label: status };
};

const resolvePhotoSrc = (photo) => {
  if (!photo) return null;
  return photo.startsWith('data:') ? photo : `data:image/jpeg;base64,${photo}`;
};

const renderWRIcon = (iconType) => {
  if (!iconType) return null;
  const iconProps = { className: "w-4 h-4 inline mr-1" };
  switch(iconType) {
    case 'Clock': return <Clock {...iconProps} />;
    case 'FileCheck': return <FileCheck {...iconProps} />;
    case 'CheckCircle': return <CheckCircle {...iconProps} />;
    case 'Truck': return <Truck {...iconProps} />;
    case 'XCircle': return <XCircle {...iconProps} />;
    default: return null;
  }
};

const ClientPackages = ({ token, trackings, warehouseReceipts }) => {
  const [photoModal, setPhotoModal] = useState({ open: false, tracking: null });
  const [trackingSearch, setTrackingSearch] = useState('');

  const searchedTracking = trackingSearch 
    ? trackings.find(t => t.tracking_number.toLowerCase().includes(trackingSearch.toLowerCase()))
    : null;

  return (
    <div className="space-y-6">
      {/* Packages List */}
      <Card>
        <CardHeader>
          <CardTitle>Mis Paquetes ({trackings.length})</CardTitle>
          <CardDescription>Todos los paquetes en tu casillero</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {trackings.map(tracking => (
              <div key={tracking.id} className="border p-4 rounded-lg" data-testid="tracking-item">
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <p className="font-semibold text-lg">{tracking.tracking_number}</p>
                    {tracking.package_number && (
                      <p className="text-sm text-gray-700" data-testid="package-number">
                        <span className="text-gray-500">Nro. Paquete:</span>{' '}
                        <span className="font-mono font-semibold">{tracking.package_number}</span>
                      </p>
                    )}
                    <p className="text-sm text-gray-600">{tracking.description || 'Sin descripcion'}</p>
                    {tracking.weight && <p className="text-sm text-gray-500">Peso: {tracking.weight} lb</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    {(tracking.photo_package || tracking.photo_label) && (
                      <button
                        onClick={() => setPhotoModal({ open: true, tracking })}
                        className="relative group rounded-lg overflow-hidden border-2 border-orange-200 hover:border-orange-400 transition-colors"
                        title="Ver fotos del paquete"
                        data-testid="package-thumbnail"
                      >
                        <img
                          src={resolvePhotoSrc(tracking.photo_package || tracking.photo_label)}
                          alt="Foto del paquete"
                          className="w-14 h-14 object-cover"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 flex items-center justify-center transition-colors">
                          <Camera className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </button>
                    )}
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      tracking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      tracking.status === 'pending_reception' ? 'bg-orange-100 text-orange-800' :
                      tracking.status === 'received' ? 'bg-blue-100 text-blue-800' :
                      tracking.status === 'in_warehouse' ? 'bg-blue-100 text-blue-800' :
                      tracking.status === 'in_repack' ? 'bg-purple-100 text-purple-800' :
                      tracking.status === 'shipped' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {tracking.status === 'pending' ? 'Pendiente' :
                       tracking.status === 'pending_reception' ? 'En transito al almacen' :
                       tracking.status === 'received' ? 'Recibido en almacen' :
                       tracking.status === 'in_warehouse' ? 'En almacen' :
                       tracking.status === 'in_repack' ? 'En reempaque' :
                       tracking.status === 'shipped' ? 'Enviado' :
                       tracking.status === 'delivered' ? 'Entregado' : tracking.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            {trackings.length === 0 && (
              <div className="text-center py-12">
                <Package className="mx-auto h-12 w-12 text-gray-400" />
                <p className="text-gray-500 text-lg mt-4">No tienes paquetes registrados</p>
                <p className="text-gray-400 text-sm mt-2">Los trackings apareceran aqui cuando el administrador te los asigne</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Photo Viewer Modal */}
      {photoModal.open && photoModal.tracking && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b bg-orange-50">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Fotos del Paquete</h2>
                <p className="text-sm text-gray-600">{photoModal.tracking.tracking_number}</p>
              </div>
              <button onClick={() => setPhotoModal({ open: false, tracking: null })} className="p-2 hover:bg-orange-100 rounded-full transition-colors">
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-700 flex items-center gap-2">Foto del Paquete</h3>
                  {photoModal.tracking.photo_package ? (
                    <div className="border rounded-lg overflow-hidden bg-gray-100">
                      <img src={photoModal.tracking.photo_package.startsWith('data:') ? photoModal.tracking.photo_package : `data:image/jpeg;base64,${photoModal.tracking.photo_package}`} alt="Foto del paquete" className="w-full h-auto object-contain max-h-[400px]" />
                    </div>
                  ) : (
                    <div className="border-2 border-dashed rounded-lg p-8 text-center bg-gray-50">
                      <Camera className="mx-auto h-12 w-12 text-gray-300 mb-2" /><p className="text-gray-400">Sin foto del paquete</p>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-700 flex items-center gap-2">Foto de la Etiqueta</h3>
                  {photoModal.tracking.photo_label ? (
                    <div className="border rounded-lg overflow-hidden bg-gray-100">
                      <img src={photoModal.tracking.photo_label.startsWith('data:') ? photoModal.tracking.photo_label : `data:image/jpeg;base64,${photoModal.tracking.photo_label}`} alt="Foto de la etiqueta" className="w-full h-auto object-contain max-h-[400px]" />
                    </div>
                  ) : (
                    <div className="border-2 border-dashed rounded-lg p-8 text-center bg-gray-50">
                      <Camera className="mx-auto h-12 w-12 text-gray-300 mb-2" /><p className="text-gray-400">Sin foto de etiqueta</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-2">Informacion del Paquete</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-500">Nro. Paquete:</span><p className="font-medium font-mono">{photoModal.tracking.package_number || 'N/A'}</p></div>
                  <div><span className="text-gray-500">Descripcion:</span><p className="font-medium">{photoModal.tracking.description || 'Sin descripcion'}</p></div>
                  <div><span className="text-gray-500">Peso:</span><p className="font-medium">{photoModal.tracking.weight ? `${photoModal.tracking.weight} lb` : 'N/A'}</p></div>
                  <div><span className="text-gray-500">Estado:</span><p className="font-medium">{photoModal.tracking.status}</p></div>
                  <div><span className="text-gray-500">Fecha:</span><p className="font-medium">{new Date(photoModal.tracking.created_at).toLocaleDateString()}</p></div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end">
              <button onClick={() => setPhotoModal({ open: false, tracking: null })} className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Warehouse Receipts
export const ClientWarehouseReceipts = ({ token, warehouseReceipts }) => (
  <Card>
    <CardHeader>
      <CardTitle>Mis Warehouse Receipts ({warehouseReceipts.length})</CardTitle>
      <CardDescription>Documentos de consolidacion generados por el Master</CardDescription>
    </CardHeader>
    <CardContent>
      <div className="space-y-4">
        {warehouseReceipts.map(wr => (
          <div key={wr.id} className="border-2 p-4 rounded-lg bg-gray-50">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="text-xl font-bold text-blue-600">{wr.wr_number}</h3>
                <p className="text-sm text-gray-500">Creado: {new Date(wr.created_at).toLocaleDateString()}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${getWRStatusStyle(wr.status).bg} ${getWRStatusStyle(wr.status).text}`}>
                {renderWRIcon(getWRStatusStyle(wr.status).iconType)}
                {getWRStatusStyle(wr.status).label}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <p className="text-sm text-gray-600 font-semibold">Trackings consolidados:</p>
                <p className="text-sm">{wr.tracking_numbers.length} paquete(s)</p>
                <ul className="text-xs text-gray-500 mt-1">{wr.tracking_numbers.map(tn => <li key={tn}>- {tn}</li>)}</ul>
              </div>
              <div>
                <p className="text-sm text-gray-600 font-semibold">Metodo de envio:</p>
                <p className="text-sm">{wr.shipping_method}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 bg-white p-3 rounded border">
              <div><p className="text-xs text-gray-600">Peso</p><p className="font-semibold">{wr.final_weight_lb} lb</p></div>
              <div><p className="text-xs text-gray-600">Dimensiones (pulgadas)</p><p className="font-semibold">{wr.final_dimensions_in}</p></div>
            </div>
            {wr.notes && <div className="mt-3 bg-yellow-50 border border-yellow-200 p-2 rounded"><p className="text-xs text-gray-700"><strong>Notas:</strong> {wr.notes}</p></div>}
            <div className="mt-3">
              <Button size="sm" variant="outline" onClick={async () => {
                try {
                  const response = await axios.get(`${API}/client/warehouse-receipts/${wr.id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a'); link.href = url; link.setAttribute('download', `WR_${wr.wr_number}.pdf`);
                  document.body.appendChild(link); link.click(); link.remove();
                } catch (error) { alert('Error al descargar PDF'); }
              }} className="w-full">Descargar PDF</Button>
            </div>
          </div>
        ))}
        {warehouseReceipts.length === 0 && (
          <div className="text-center py-12">
            <Package className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <p className="text-gray-500 text-lg">No tienes Warehouse Receipts</p>
            <p className="text-gray-400 text-sm mt-2">Apareceran aqui cuando el Master procese tus reempaques</p>
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

// Track Package
export const ClientTrackPackage = ({ trackings }) => {
  const [trackingSearch, setTrackingSearch] = useState('');
  const searchedTracking = trackingSearch ? trackings.find(t => t.tracking_number.toLowerCase().includes(trackingSearch.toLowerCase())) : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Rastrear Paquete</CardTitle>
        <CardDescription>Busca tu paquete por numero de tracking</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Numero de Tracking</Label>
            <Input value={trackingSearch} onChange={(e) => setTrackingSearch(e.target.value)} placeholder="Ingresa el numero de tracking..." />
          </div>
          {searchedTracking && (
            <div className="border-2 border-blue-500 p-6 rounded-lg bg-blue-50">
              <h3 className="text-xl font-bold mb-4">{searchedTracking.tracking_number}</h3>
              <div className="space-y-2">
                {searchedTracking.package_number && (
                  <div className="flex justify-between"><span className="text-gray-600">Nro. Paquete:</span><span className="font-semibold font-mono">{searchedTracking.package_number}</span></div>
                )}
                <div className="flex justify-between"><span className="text-gray-600">Descripcion:</span><span className="font-semibold">{searchedTracking.description || 'N/A'}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Peso:</span><span className="font-semibold">{searchedTracking.weight ? `${searchedTracking.weight} lb` : 'N/A'}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Estado:</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${searchedTracking.status === 'received' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>{searchedTracking.status}</span>
                </div>
                <div className="flex justify-between"><span className="text-gray-600">Fecha de registro:</span><span className="font-semibold">{new Date(searchedTracking.created_at).toLocaleDateString()}</span></div>
              </div>
            </div>
          )}
          {trackingSearch && !searchedTracking && <div className="text-center py-8 text-gray-500"><p>No se encontro ningun paquete con ese numero de tracking</p></div>}
          {!trackingSearch && <div className="text-center py-8 text-gray-400"><Package size={48} className="mx-auto mb-2 opacity-50" /><p>Ingresa un numero de tracking para buscar</p></div>}
        </div>
      </CardContent>
    </Card>
  );
};

export default ClientPackages;
